import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, useLocation, useRoutes } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';

import { pageTransitionVariants } from '@/animations';
import CacheManager from '@/components/CacheManager';
import ErrorBoundary from '@/components/ErrorBoundary';
import ScrollToTop from '@/components/ScrollToTop';
import WipeOverlay from '@/components/WipeOverlay';
import { AuthProvider } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { useTabTitle } from '@/hooks/useTabTitle';
import MainLayout from '@/layout/MainLayout';
import { routes } from '@/routes';
import { OrganizationJsonLd, WebSiteJsonLd } from '@/seo';
import { useCartStore } from '@/store/cartStore';
import { useWishlistStore } from '@/store/wishlistStore';

import { CartCountProvider } from './context/CartCountContext';
import { WishlistCountProvider } from './context/WishlistCountContext';

function AnimatedRoutes() {
  const location = useLocation();
  const element = useRoutes(routes);

  return (
    <>
      <WipeOverlay key={location.pathname} />

      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          variants={pageTransitionVariants}
          initial="initial"
          animate="animate"
          exit="exit">
          {element}
        </motion.div>
      </AnimatePresence>
    </>
  );
}

function App() {
  useTabTitle();

  useEffect(() => {
    void useCartStore.getState().fetchCart();
  }, []);

  useEffect(() => {
    const items = useWishlistStore.getState().items;
    if (items.length === 0) {
      void useWishlistStore.getState().fetchWishlist();
    }
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider>
        <ErrorBoundary>
          <OrganizationJsonLd />
          <WebSiteJsonLd />
          <Router>
            <ScrollToTop />
            <CacheManager />
            <AuthProvider>
              <CartCountProvider>
                <WishlistCountProvider>
                  <MainLayout>
                    <AnimatedRoutes />
                  </MainLayout>
                </WishlistCountProvider>
              </CartCountProvider>
            </AuthProvider>
          </Router>
        </ErrorBoundary>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
